/**
 * 한 번 더 묻기 말풍선 (common.css .confirm-pop).
 *
 * 알림창(window.confirm) 대신 누른 버튼 위에 "내용을 지울까요? 삭제 취소" 를 띄운다.
 * [삭제]면 true, [취소] · 바깥 클릭 · Esc · 화면 크기 변경이면 false 로 끝나는 Promise 를 돌려준다.
 * 한 번에 하나만 뜨고, 다른 말풍선을 열면 앞의 것은 취소로 닫힌다.
 * 열리면 [취소]에 초점을 두어(Enter 로 실수로 지우지 않게) 닫히면 누른 버튼으로 초점을 돌려준다.
 *
 *   if (await confirmPop(button, { message: '내용을 지울까요?', confirmLabel: '삭제' })) { ... }
 */

const GAP = 10;
const EDGE = 16;

let closeOpen = null;

const confirmPop = (
  anchor,
  { message = '정말 할까요?', confirmLabel = '삭제', cancelLabel = '취소' } = {}
) => {
  closeOpen?.(false);

  return new Promise((resolve) => {
    const pop = document.createElement('div');
    pop.className = 'confirm-pop';
    pop.setAttribute('role', 'alertdialog');
    pop.setAttribute('aria-label', message);

    const text = document.createElement('span');
    text.className = 'confirm-pop__message';
    text.textContent = message;

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'confirm-pop__action confirm-pop__action--danger';
    confirm.textContent = confirmLabel;

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'confirm-pop__action';
    cancel.textContent = cancelLabel;

    pop.append(text, confirm, cancel);
    document.body.append(pop);

    // 버튼 위 가운데. 위에 자리가 없으면 아래로, 좌우는 화면 안으로 밀어 넣고 꼬리만 버튼을 가리킨다
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const width = pop.offsetWidth;
      const height = pop.offsetHeight;
      const below = rect.top - height - GAP < EDGE;
      const center = rect.left + rect.width / 2;
      const left = Math.min(
        Math.max(center - width / 2, EDGE),
        document.documentElement.clientWidth - width - EDGE
      );
      const top = below ? rect.bottom + GAP : rect.top - height - GAP;

      pop.classList.toggle('confirm-pop--below', below);
      pop.style.left = `${left + window.scrollX}px`;
      pop.style.top = `${top + window.scrollY}px`;
      pop.style.setProperty('--confirm-pop-arrow', `${center - left}px`);
    };

    const onPointer = (event) => {
      if (!pop.contains(event.target) && !anchor.contains(event.target)) {
        close(false);
      }
    };

    const onKey = (event) => {
      if ('Escape' === event.key) {
        event.preventDefault();
        close(false, true);
      }
    };

    const onResize = () => close(false);

    function close(result, restoreFocus = result || document.activeElement === document.body) {
      if (closeOpen !== close) {
        return;
      }

      closeOpen = null;
      pop.remove();
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);

      if (restoreFocus && anchor.isConnected) {
        anchor.focus();
      }

      resolve(result);
    }

    closeOpen = close;
    place();

    confirm.addEventListener('click', () => close(true));
    cancel.addEventListener('click', () => close(false, true));
    // 여는 클릭이 곧바로 바깥 클릭으로 잡히지 않게 다음 틱에 붙인다
    window.setTimeout(() => {
      if (closeOpen === close) {
        document.addEventListener('pointerdown', onPointer, true);
      }
    });
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);

    cancel.focus();
  });
};

export default confirmPop;
